import Header from 'components/Header/Header';
import Flex from 'components/ui/Flex';
import useAppDispatch from 'hooks/useAppDispatch';
import useAppSelector from 'hooks/useAppSelector';
import { useCallback, useEffect, useRef } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { BatchReturnStatusType, ReturnStatusType } from 'returnCenter-api';
import { companiesSelector } from 'store/reducers/companiesSlice';
import { clearErrors, returnSelectors, returnThunks } from 'store/reducers/returnSlice';
import { userSelector } from 'store/reducers/usersSlice';
import { getBatchStatusTypeString, getStatusTypeString } from 'utils/helpers';
import ActionsBar from './ActionBar';
import { ContentContainer } from 'components/ui/ContentContainer';
import {
    selectCurrentView,
    selectSelectedStatus,
    setOrigin,
    setSelectedStatus,
    setSelectedBatchStatus,
    setViewMode,
} from 'store/reducers/navigationSlice';
import { fetchBatchReturnStatistics } from 'store/reducers/batchReturnSlice';
import useGoBackNavigation from 'hooks/useGoBackNavigation';
import useScannerDispatch from 'hooks/useScannerDispatch';
import type { SearchMode } from 'components/Search/SearchModal';
import type { PendingSingleItem } from 'hooks/scannerDispatch/types';
import { isBatchRoute, isHomeRoute, isReturnHome } from 'utils/navigation';

export type LayoutOutletContext = {
    incrementRef: { current: ((scan: string) => void) | undefined };
    setMaxQtyRef: { current: (() => void) | undefined };
    openModalRef: { current: ((searchMode: SearchMode) => void) | undefined };
    closeModalRef: { current: (() => void) | undefined };
    openDeleteDialogRef: { current: (() => void) | undefined };
    confirmDeleteDialogRef: { current: (() => void) | undefined };
    cancelScannerRef: { current: (() => void) | undefined };
    bulkReasonApplyRef: { current: ((value: number) => void) | undefined };
    pendingSingleItemRef: { current: PendingSingleItem | null };
    confirmNavDialogRef: { current: (() => void) | undefined };
    cancelDeleteDialogRef: { current: (() => void) | undefined };
    cancelNavigationRef: { current: (() => void) | undefined };
    highlightedKey?: string | null;
    clearHighlight?: () => void;
};

const Layout = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const backToOrderList = useGoBackNavigation();

    const statistics = useAppSelector(returnSelectors.selectReturnStatistics);
    const currentCompany = useAppSelector(companiesSelector.selectCurrentCompany);
    const userProfile = useAppSelector(userSelector.selectUserProfile);
    const {
        completed,
        onHold,
        inprogress,
        incomingPackage,
        received,
        created,
        itemsSelected,
        creditError,
        creditedBatchPending,
    } = useAppSelector(returnSelectors.selectLoadingCards);

    const currentView = useAppSelector(selectCurrentView);
    const selectedStatus = useAppSelector(selectSelectedStatus);

    const pathname = location.pathname;

    const returnHome = isReturnHome(pathname);
    const batchRoute = isBatchRoute(pathname);
    const isHome = isHomeRoute(pathname);

    useEffect(() => {
        if (batchRoute) {
            dispatch(setViewMode('batch-returns'));
            dispatch(fetchBatchReturnStatistics());
        } else {
            dispatch(setViewMode('returns'));
            dispatch(returnThunks.getReturnStatistics());
        }
    }, [batchRoute, dispatch, currentCompany, userProfile, currentView, selectedStatus]);

    useEffect(() => {
        if (!location.pathname.startsWith('/returns')) {
            dispatch(setSelectedStatus(null));
        }
    }, [location.pathname, dispatch]);

    // Refs registered by child pages via Outlet context
    const incrementRef = useRef<((scan: string) => void) | undefined>(undefined);
    const setMaxQtyRef = useRef<(() => void) | undefined>(undefined);
    const openModalRef = useRef<((searchMode: SearchMode) => void) | undefined>(undefined);
    const closeModalRef = useRef<(() => void) | undefined>(undefined);
    const openDeleteDialogRef = useRef<(() => void) | undefined>(undefined);
    const confirmDeleteDialogRef = useRef<(() => void) | undefined>(undefined);
    const cancelScannerRef = useRef<(() => void) | undefined>(undefined);
    const bulkReasonApplyRef = useRef<((value: number) => void) | undefined>(undefined);
    const pendingSingleItemRef = useRef<PendingSingleItem | null>(null);
    const confirmNavDialogRef = useRef<(() => void) | undefined>(undefined);
    const cancelDeleteDialogRef = useRef<(() => void) | undefined>(undefined);
    const cancelNavigationRef = useRef<(() => void) | undefined>(undefined);

    const { highlightedKey, clearHighlight, cancelPending } = useScannerDispatch({
        incrementRef,
        setMaxQtyRef,
        openModalRef,
        closeModalRef,
        openDeleteDialogRef,
        confirmDeleteDialogRef,
        bulkReasonApplyRef,
        pendingSingleItemRef,
        confirmNavDialogRef,
        cancelDeleteDialogRef,
        cancelNavigationRef,
    });

    // Expose cancelPending to child pages via ref
    cancelScannerRef.current = cancelPending;

    const handleStatusCardClick = useCallback(
        (statusType?: ReturnStatusType, _value?: number) => {
            dispatch(clearErrors());
            dispatch(setSelectedStatus(statusType ?? null));
            dispatch(setOrigin(null));

            if (statusType !== undefined) {
                navigate(`/returns/${getStatusTypeString(statusType)}`);
            }
        },
        [navigate, dispatch],
    );

    const handleBatchStatusCardClick = useCallback(
        (statusType: BatchReturnStatusType) => {
            dispatch(setSelectedBatchStatus(statusType));
            navigate(`/batch-returns/${getBatchStatusTypeString(statusType)}`);
        },
        [navigate, dispatch],
    );

    return (
        <Flex className="layout-wrapper mb-5" column $grow={1}>
            <Header />
            <Flex $grow={1} className="main-content w-100">
                <Flex className="content-container w-100" column $grow={1} gap="1rem">
                    <ActionsBar
                        handleBack={backToOrderList}
                        statistics={statistics}
                        completed={completed}
                        blocked={onHold}
                        inprogress={inprogress}
                        created={created}
                        itemsSelected={itemsSelected}
                        incomingPackage={incomingPackage}
                        received={received}
                        creditError={creditError}
                        creditedBatchPending={creditedBatchPending}
                        selectedStatus={selectedStatus}
                        onStatusCardClick={handleStatusCardClick}
                        onBatchStatusCardClick={handleBatchStatusCardClick}
                        isHome={isHome}
                        isReturnHome={returnHome}
                    />
                    <ContentContainer $grow={1} className="outlet-wrapper">
                        <Outlet
                            context={{
                                incrementRef,
                                setMaxQtyRef,
                                openModalRef,
                                closeModalRef,
                                openDeleteDialogRef,
                                confirmDeleteDialogRef,
                                cancelScannerRef,
                                highlightedKey,
                                clearHighlight,
                                bulkReasonApplyRef,
                                pendingSingleItemRef,
                                confirmNavDialogRef,
                                cancelDeleteDialogRef,
                                cancelNavigationRef,
                            }}
                        />
                    </ContentContainer>
                </Flex>
            </Flex>
        </Flex>
    );
};

export default Layout;
